/**
 * Registration consent stash.
 *
 * /join collects TOS acceptance and SMS opt-in BEFORE the user has an
 * account — there's nothing to write to the DB yet. We stash both the
 * checkbox values and the click timestamp in sessionStorage, then read
 * them back in /auth/callback once the user is authenticated and write
 * everything atomically via a single mutation.
 *
 * Why timestamp on the client: the legally-binding moment is when the
 * customer clicked "I agree", not when our server happened to record it.
 * If the post-auth mutation fails, we don't want a retry to log a fresh
 * NOW() that misrepresents when consent was given.
 *
 * sessionStorage scope is per-tab. WorkOS sign-in is a same-tab redirect,
 * so the value survives the round-trip. If the customer abandons mid-flow
 * the value naturally clears with the tab.
 */
const KEY = 'esig-registration-consent';

export interface RegistrationConsent {
  /** ISO-8601 timestamp of when the user ticked TOS */
  tosAcceptedAt: string;
  /** Whether the user opted in to non-OTP SMS notifications */
  smsConsent: boolean;
}

export function stashRegistrationConsent(consent: RegistrationConsent): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(consent));
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.). Consent
    // can't be threaded through; the user will need to re-confirm on
    // their next session. Acceptable degradation.
  }
}

export function consumeRegistrationConsent(): RegistrationConsent | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RegistrationConsent>;
    if (typeof parsed.tosAcceptedAt !== 'string') return null;
    if (typeof parsed.smsConsent !== 'boolean') return null;
    // Validate the timestamp parses; reject garbage.
    if (Number.isNaN(Date.parse(parsed.tosAcceptedAt))) return null;
    return parsed as RegistrationConsent;
  } catch {
    return null;
  }
}

/**
 * Peek without consuming. Used by retry paths (e.g., a top-level
 * effect that re-fires the consent mutation if the callback handoff
 * failed).
 */
export function peekRegistrationConsent(): RegistrationConsent | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RegistrationConsent>;
    if (typeof parsed.tosAcceptedAt !== 'string') return null;
    if (typeof parsed.smsConsent !== 'boolean') return null;
    if (Number.isNaN(Date.parse(parsed.tosAcceptedAt))) return null;
    return parsed as RegistrationConsent;
  } catch {
    return null;
  }
}
